import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../layouts/default";
import { Button, Spinner, Table, Row, Col, Form, Modal } from "react-bootstrap";
import SmartPagination from "../Report/EmailCampaignRunReport/DynamicSmartPagination";
import {
  fetchEmailCategories,
  createEmailCategory,
  updateEmailCategory,
  deleteEmailCategory,
  formatVendorSplit,
} from "../../services/emailCategories";
import { fetchEmailVendors } from "../../services/emailVendors";
import { toast } from "react-toastify";
import { hasPermission } from "../../services/utils";
import { PERMISSIONS } from "../../contants/permission";

const emptyForm = () => ({
  id: null,
  name: "",
  vendors: [{ emailVendorId: "", weight: 100 }],
});

const limit = 10;

function EmailCategories() {
  const [list, setList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const [appliedName, setAppliedName] = useState("");

  const [activeVendors, setActiveVendors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const userId = () => JSON.parse(localStorage.getItem("user"))?.data?.id;

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchEmailCategories({
      limit,
      page,
      filters: { name: appliedName },
    });
    setLoading(false);
    if (!res?.success) {
      toast.error(res?.message || "Failed to load email categories");
      return;
    }
    setList(res.data?.records || []);
    setTotalPages(res.data?.totalPages || 0);
  }, [page, appliedName]);

  useEffect(() => {
    load();
  }, [load]);

  const loadActiveVendors = async () => {
    const res = await fetchEmailVendors({
      limit: 100,
      page: 1,
      filters: { is_active: true },
    });
    if (!res?.success) {
      toast.error(res?.message || "Failed to load email vendors");
      return;
    }
    setActiveVendors(res.data?.records || []);
  };

  const totalWeight = useMemo(
    () => form.vendors.reduce((s, v) => s + (Number(v.weight) || 0), 0),
    [form.vendors]
  );

  const formError = useMemo(() => {
    if (!form.name.trim()) return "Category name is required";
    if (!form.vendors.length) return "At least one email vendor is required";
    if (form.vendors.some((v) => !v.emailVendorId)) {
      return "Select a vendor in every row";
    }
    if (
      form.vendors.some(
        (v) =>
          !Number.isInteger(Number(v.weight)) ||
          Number(v.weight) <= 0 ||
          Number(v.weight) > 100
      )
    ) {
      return "Each weight must be a whole number between 1 and 100";
    }
    const ids = form.vendors.map((v) => String(v.emailVendorId));
    if (new Set(ids).size !== ids.length) {
      return "The same vendor cannot be added twice";
    }
    if (totalWeight !== 100) {
      return "Total weight must be exactly 100%";
    }
    return null;
  }, [form, totalWeight]);

  const openCreate = () => {
    setForm(emptyForm());
    loadActiveVendors();
    setShowModal(true);
  };

  const openEdit = (c) => {
    setForm({
      id: c.id,
      name: c.name,
      vendors: (c.vendors || []).map((v) => ({
        emailVendorId: String(v.id),
        weight: v.weight,
      })),
    });
    loadActiveVendors();
    setShowModal(true);
  };

  const setVendorRow = (index, patch) =>
    setForm({
      ...form,
      vendors: form.vendors.map((v, i) => (i === index ? { ...v, ...patch } : v)),
    });

  const addVendorRow = () =>
    setForm({
      ...form,
      vendors: [
        ...form.vendors,
        { emailVendorId: "", weight: form.vendors.length ? "" : 100 },
      ],
    });

  const removeVendorRow = (index) =>
    setForm({ ...form, vendors: form.vendors.filter((_, i) => i !== index) });

  const vendorOptionsFor = (index) => {
    const taken = new Set(
      form.vendors
        .filter((_, i) => i !== index)
        .map((v) => String(v.emailVendorId))
    );
    const options = activeVendors.filter((v) => !taken.has(String(v.id)));
    const current = form.vendors[index]?.emailVendorId;
    const listed = options.some((v) => String(v.id) === String(current));
    if (current && !listed) {
      const original = list
        .find((c) => c.id === form.id)
        ?.vendors?.find((v) => String(v.id) === String(current));
      if (original) {
        options.push({
          id: original.id,
          name: `${original.name}${original.is_active ? "" : " (inactive)"}`,
        });
      }
    }
    return options;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (formError) return toast.error(formError);
    const vendors = form.vendors.map((v) => ({
      emailVendorId: Number(v.emailVendorId),
      weight: Number(v.weight),
    }));
    setSaving(true);
    const res = form.id
      ? await updateEmailCategory({
          id: form.id,
          name: form.name.trim(),
          vendors,
          updatedBy: userId(),
        })
      : await createEmailCategory({
          name: form.name.trim(),
          vendors,
          createdBy: userId(),
        });
    setSaving(false);
    if (!res?.success) return toast.error(res?.message || "Failed to save");
    toast.success(form.id ? "Email category updated" : "Email category created");
    setShowModal(false);
    load();
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    const res = await deleteEmailCategory(c.id);
    if (!res?.success) return toast.error(res?.message || "Failed to delete");
    toast.success("Email category deleted");
    if (list.length === 1 && page > 1) setPage(page - 1);
    else load();
  };

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center my-3 px-3">
        <h4 className="fw-semibold mb-0">Email Categories</h4>
        {hasPermission(PERMISSIONS.EMAIL_CATEGORY.CREATE) && (
          <Button variant="primary" onClick={openCreate}>
            + Add Category
          </Button>
        )}
      </div>

      <div className="p-3 border rounded bg-light mx-3 mb-3">
        <Row className="g-2 align-items-end">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col className="d-flex justify-content-end gap-2 ms-auto">
            <Button
              className="px-4"
              onClick={() => {
                setAppliedName(nameFilter.trim());
                setPage(1);
              }}
            >
              Apply
            </Button>
            <Button
              variant="secondary"
              className="px-4"
              onClick={() => {
                setNameFilter("");
                setAppliedName("");
                setPage(1);
              }}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </div>

      <div className="mx-3">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p>Loading...</p>
          </div>
        ) : list?.length ? (
          <>
            <Table bordered hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Vendors</th>
                  <th>Campaigns</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c, index) => (
                  <tr key={c.id}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td>{c.name}</td>
                    <td>
                      {c.vendors?.length ? (
                        <>
                          {formatVendorSplit(c.vendors)}
                          {c.vendors.some((v) => !v.is_active) && (
                            <span className="badge bg-danger ms-2">
                              Inactive vendor
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-danger">Not assigned</span>
                      )}
                    </td>
                    <td>{c.campaignCount ?? 0}</td>
                    <td>
                      {c.updatedAt
                        ? new Date(c.updatedAt).toLocaleString()
                        : "NA"}
                    </td>
                    <td className="d-flex gap-1 flex-wrap">
                      {hasPermission(PERMISSIONS.EMAIL_CATEGORY.EDIT) && (
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => openEdit(c)}
                        >
                          Edit
                        </Button>
                      )}
                      {hasPermission(PERMISSIONS.EMAIL_CATEGORY.DELETE) && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(c)}
                        >
                          Delete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <SmartPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={(p) => setPage(p)}
            />
          </>
        ) : (
          <p className="text-center text-muted py-4">No email categories found</p>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {form.id ? "Edit Email Category" : "Add Email Category"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Category Name *</Form.Label>
              <Form.Control
                value={form.name}
                maxLength={100}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Label className="mb-1">Email Vendors *</Form.Label>
            {form.vendors.map((v, index) => (
              <Row className="g-2 mb-2 align-items-center" key={index}>
                <Col xs={6}>
                  <Form.Select
                    value={v.emailVendorId}
                    onChange={(e) =>
                      setVendorRow(index, { emailVendorId: e.target.value })
                    }
                  >
                    <option value="">Select vendor</option>
                    {vendorOptionsFor(index).map((o) => (
                      <option key={o.id} value={String(o.id)}>
                        {o.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={4}>
                  <div className="input-group">
                    <Form.Control
                      type="number"
                      min={1}
                      max={100}
                      value={v.weight}
                      onChange={(e) =>
                        setVendorRow(index, { weight: e.target.value })
                      }
                    />
                    <span className="input-group-text">%</span>
                  </div>
                </Col>
                <Col xs={2}>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={form.vendors.length === 1}
                    onClick={() => removeVendorRow(index)}
                  >
                    ✕
                  </Button>
                </Col>
              </Row>
            ))}
            <div className="d-flex justify-content-between align-items-center mt-2">
              <Button
                size="sm"
                variant="outline-primary"
                disabled={form.vendors.length >= activeVendors.length}
                onClick={addVendorRow}
              >
                + Add Vendor
              </Button>
              {form.vendors.length > 0 && (
                <span
                  className={
                    totalWeight === 100 ? "text-success" : "text-danger"
                  }
                >
                  Total Weight: {totalWeight}%
                </span>
              )}
            </div>
            {formError && form.name.trim() && (
              <p className="text-danger small mt-2 mb-0">{formError}</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving || !!formError}
            >
              {saving ? "Saving…" : form.id ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Layout>
  );
}

export default EmailCategories;
